import { Injectable, Inject, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { Router } from '@angular/router';

export interface SeoConfig {
  title?:          string;
  description?:    string;
  keywords?:       string;
  image?:          string;
  url?:            string;
  type?:           'website' | 'article' | 'product';
  noindex?:        boolean;
  jsonLd?:         Record<string, any>;
  twitterHandle?:  string;   // e.g. 'sale4all'  (without @)
  siteName?:       string;   // override SITE_NAME constant
  canonicalBase?:  string;   // override SITE_URL constant
}

// ── Hard-coded fallbacks (used only when API hasn't loaded yet) ──────────────
const FALLBACK_SITE_NAME    = 'Sale4ALL';
const FALLBACK_SITE_URL     = 'https://sale4all.co.il';
const FALLBACK_DEFAULT_IMG  = '/assets/img/og-default.jpg';
const FALLBACK_DEFAULT_DESC = 'פלטפורמת המכירות של ישראל — רכבים, נדל"ן, אלקטרוניקה ועוד. מצא, פרסם וקנה בקלות.';
const FALLBACK_DEFAULT_KW   = 'מכירות, רכבים, נדל"ן, אלקטרוניקה, ישראל, מודעות, קנייה, מכירה';

@Injectable({ providedIn: 'root' })
export class MetaTagService {

  // ── Live values — populated by init() from the company-API ─────────────────
  private _siteName:       string = FALLBACK_SITE_NAME;
  private _siteUrl:        string = FALLBACK_SITE_URL;
  private _defaultImg:     string = FALLBACK_DEFAULT_IMG;
  private _defaultDesc:    string = FALLBACK_DEFAULT_DESC;
  private _defaultKw:      string = FALLBACK_DEFAULT_KW;
  private _twitterHandle:  string = 'sale4all';

  constructor(
    private meta:    Meta,
    private titleSvc: Title,
    private router:  Router,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  /**
   * Called once on app startup (from AppComponent.ngOnInit) after the
   * company-API response arrives.  All per-page seo.update() calls that happen
   * after this will automatically use the live values.
   */
  init(cfg: {
    siteName?:      string;
    siteUrl?:       string;
    defaultImg?:    string;
    defaultDesc?:   string;
    defaultKw?:     string;
    twitterHandle?: string;
  }): void {
    if (cfg.siteName)      { this._siteName      = cfg.siteName; }
    if (cfg.siteUrl)       { this._siteUrl        = cfg.siteUrl.replace(/\/$/, ''); }
    if (cfg.defaultImg)    { this._defaultImg     = cfg.defaultImg; }
    if (cfg.defaultDesc)   { this._defaultDesc    = cfg.defaultDesc; }
    if (cfg.defaultKw)     { this._defaultKw      = cfg.defaultKw; }
    if (cfg.twitterHandle) { this._twitterHandle  = cfg.twitterHandle.replace(/^@/, ''); }
  }

  /**
   * Returns the full canonical URL for a given app path.
   * Usage in page components:  url: this.seo.pageUrl('/about')
   */
  pageUrl(path: string = ''): string {
    const base = this._siteUrl;
    const p    = path.startsWith('/') ? path : `/${path}`;
    return `${base}${p}`;
  }

  /** Expose site name so page components can embed it in descriptions */
  get siteName(): string { return this._siteName; }

  // ── Main entry point ────────────────────────────────────────────────────────

  update(cfg: SeoConfig = {}): void {
    const siteName    = cfg.siteName     || this._siteName;
    const siteUrl     = cfg.canonicalBase || this._siteUrl;
    const title       = cfg.title ? `${cfg.title} | ${siteName}` : siteName;
    const description = cfg.description || this._defaultDesc;
    const image       = cfg.image       || this._resolveImg(this._defaultImg);
    const url         = cfg.url         || `${siteUrl}${this.router.url}`;
    const type        = cfg.type        || 'website';
    const twitterAt   = cfg.twitterHandle
                          ? `@${cfg.twitterHandle.replace(/^@/, '')}`
                          : `@${this._twitterHandle}`;

    // Basic
    this.titleSvc.setTitle(title);
    this._name('description',  description);
    this._name('keywords',     cfg.keywords || this._defaultKw);
    this._name('robots',       cfg.noindex ? 'noindex,nofollow' : 'index,follow');
    this._name('author',       siteName);

    // Open Graph — Facebook, WhatsApp, LinkedIn, Telegram
    this._prop('og:site_name',   siteName);
    this._prop('og:type',        type);
    this._prop('og:url',         url);
    this._prop('og:title',       title);
    this._prop('og:description', description);
    this._prop('og:image',       image);
    this._prop('og:image:width',  '1200');
    this._prop('og:image:height', '630');
    this._prop('og:image:alt',    title);
    this._prop('og:locale',      'he_IL');

    // Twitter / X Card
    this._name('twitter:card',        'summary_large_image');
    this._name('twitter:site',        twitterAt);
    this._name('twitter:title',       title);
    this._name('twitter:description', description);
    this._name('twitter:image',       image);
    this._name('twitter:image:alt',   title);

    // Canonical
    this._canonical(url);

    // JSON-LD
    if (cfg.jsonLd) { this._jsonLd(cfg.jsonLd); }
  }

  /** Convenience: set SEO for a product/listing page */
  updateForProduct(p: {
    title:        string;
    description:  string;
    image?:       string;
    price?:       number;
    currency?:    string;
  }): void {
    const image = p.image || this._resolveImg(this._defaultImg);
    this.update({
      title:       p.title,
      description: p.description,
      image,
      type:        'product',
      jsonLd: {
        '@context':    'https://schema.org',
        '@type':       'Product',
        'name':        p.title,
        'description': p.description,
        'image':       image,
        ...(p.price != null ? {
          offers: {
            '@type':         'Offer',
            'price':         p.price,
            'priceCurrency': p.currency || 'ILS',
            'availability':  'https://schema.org/InStock',
            'url':           `${this._siteUrl}${this.router.url}`,
          }
        } : {}),
      }
    });
  }

  /** Legacy shim — keeps old callers working */
  setMetaTag(name: string, content: string): void {
    if (name === 'title') { this.titleSvc.setTitle(content); }
    else { this._name(name, content); }
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  /** Turn a relative path into an absolute URL using the cached site URL */
  private _resolveImg(img: string): string {
    if (!img) { return ''; }
    if (img.startsWith('http')) { return img; }
    return `${this._siteUrl}${img.startsWith('/') ? '' : '/'}${img}`;
  }

  private _name(name: string, content: string): void {
    if (this.meta.getTag(`name='${name}'`)) {
      this.meta.updateTag({ name, content });
    } else {
      this.meta.addTag({ name, content });
    }
  }

  private _prop(property: string, content: string): void {
    if (this.meta.getTag(`property='${property}'`)) {
      this.meta.updateTag({ property, content });
    } else {
      this.meta.addTag({ property, content });
    }
  }

  private _canonical(url: string): void {
    let link = this.doc.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private _jsonLd(schema: Record<string, any>): void {
    let script = this.doc.querySelector<HTMLScriptElement>('script[type="application/ld+json"]');
    if (!script) {
      script = this.doc.createElement('script');
      script.type = 'application/ld+json';
      this.doc.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
  }
}
